<template>
  <div v-if="package !== null" class="package">
    <div class="package-id">
      <a :href="'/#/package/' + package.id" target="_blank">
        <span>{{ $t('dispatch.mapDetails.package') }} {{ package.id }}</span>
        <i class="fas fa-cube fa-fw"></i>
      </a>
    </div>

    <!-- PACKAGE INFO -->
    <div class="label-value">
      <div class="label">{{ $t('dispatch.mapDetails.packagePickup') }}</div>
      <div class="value">{{ getAddress(package.pickup) }}</div>
    </div>
    <div class="label-value">
      <div class="label">{{ $t('dispatch.mapDetails.packageDropoff') }}</div>
      <div class="value">{{ getAddress(package.dropoff) }}</div>
    </div>

    <div class="row">
      <div class="small-6 columns">
        <div class="label-value">
          <div class="label">{{ $t('dispatch.mapDetails.pickupTime') }}</div>
          <div class="value">{{ package.pickupTime }}</div>
        </div>
      </div>

      <div class="small-6 columns">
        <div class="label-value">
          <div class="label">PASSENGER/LUGGAGE/STOPS</div>
          <div class="value">{{ package.pax }}/{{ package.luggage }}/{{ package.stops }}</div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="small-6 columns">
        <div class="label-value">
          <div class="label">{{ $t('dispatch.mapDetails.serviceType') }}</div>
          <div v-bind:class="'value service-type ' + package.serviceClass">{{ package.serviceLabel }}</div>
        </div>
      </div>
      <div class="small-6 columns">
        <div class="label-value">
          <div class="label">{{ $t('dispatch.mapDetails.status') }}</div>
          <div v-bind:class="'value status ' + package.statusClass">{{ package.status }}</div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="small-6 columns">
        <div class="label-value">
          <div class="label">FARE AMOUNT</div>
          <div class="value">${{ package.fare.toFixed(2) }}</div>
        </div>
      </div>
      <div class="small-6 columns">
        <div class="label-value">
          <div class="label">DRIVER PAYMENT</div>
          <div class="value">${{ getDriverPayment(package.driverPayment).toFixed(2) }}</div>
          <a class="payment-breakdown-button" href="javascript:;"
            @click="showBreakdown(true)">See Payment Breakdown</a>
        </div>
      </div>
    </div>

    <!-- PACKAGE INFO end -->

    <!-- DRIVER INFO -->
    <div class="divider"></div>
    <div v-if="package.driver !== null">

      <div class="row">
        <div class="small-6 columns">
          <div class="label-value">
            <div class="label">{{ $t('dispatch.mapDetails.driverName') }}</div>
            <div class="value">
              <a :href="'/#/provider/' + package.driver.id" target="_blank">
                {{ package.driver.firstName }} {{ package.driver.lastName }}
              </a>
            </div>
          </div>
        </div>
        <div class="small-6 columns">
          <div class="label-value">
            <div class="label">{{ $t('dispatch.mapDetails.driverEmail') }}</div>
            <div class="value">
              <a href="javascript:;" :href="'mailto:' + package.driver.email">
                {{ package.driver.email }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- a class="button error cta small unassign" href="javascript:;">Unassign {{ package.driver.firstName }} from this package</a-->
    </div>
    <div v-else>
      <div class="label-value">
        <div class="label">{{ $t('dispatch.mapDetails.driverInfo') }}</div>
        <div class="value">
          <div class="info">{{ $t('dispatch.mapDetails.packageDriverNone') }}</div>
        </div>
      </div>
    </div>
    <!-- DRIVER INFO end -->

    <!-- STOPS -->
    <div class="divider"></div>
    <div class="stops">
      <div class="title">STOPS FOR PACKAGE {{ package.id }}</div>
      <div v-bind:class="{ stop: true, pickup: leg.isPickup }" v-for="(leg, index) in package.legs">
        <div class="name">
          <i v-bind:class="{ 'fas fa-map-marker-alt fa-fw': true, pickup: leg.isPickup }"></i></span>
          <a :href="'/#/consumer/' + leg.customer.id" target="_blank">{{ leg.customer.firstName }} {{ leg.customer.lastName }}</a>
        </div>
        <div class="time">
          <span v-if="leg.isPickup">{{ leg.pickupTime }}</span>
          <span v-else>TBD</span>
        </div>
        <div class="address">
          <span v-if="leg.isPickup">{{ getAddress(leg.pickup) }}</span>
          <span v-else>{{ getAddress(leg.dropoff) }}</span>
        </div>
        <div v-bind:class="'status ' + leg.statusClass">{{ leg.status }}</div>
        <div class="misc">
          <div class="rsvp-id">
            <span>ID:</span>
            <a :href="'/#/reservation/' + leg.id" target="_blank">{{ leg.id }}</a>
          </div>
          <div class="pax-luggage">
            <i class="fas fa-user fa-fw"></i><span>{{ leg.pax }}</span>
            <i class="fas fa-suitcase fa-fw"></i><span>{{ leg.luggage }}</span>
          </div>
        </div>
      </div>
    </div>
    <!-- STOPS end -->


    <!-- PAYMENT BREAKDOWN -->
    <div v-bind:class="{ 'modal-container': true, show: showPaymentBreakdown }" @click="showBreakdown(false)">
      <div class="modal">
        <div class="modal-content">
          <div class="payment-breakdown" @click.stop>
            <a class="close" href="javascript:;" @click="showBreakdown(false)">
              <i class="far fa-times-circle fa-fw"></i>
            </a>
            <div class="title">PAYMENT BREAKDOWN</div>
            <div class="row breakdown">
              <div class="small-6 columns label">Fare Amount:</div>
              <div class="small-6 columns value">${{ package.driverPayment.base.toFixed(2) }}</div>
            </div>
            <div class="row breakdown">
              <div class="small-6 columns label">Bonus:</div>
              <div class="small-6 columns value">${{ package.driverPayment.bonus.toFixed(2) }}</div>
            </div>
            <div class="row breakdown">
              <div class="small-6 columns label">Tip:</div>
              <div class="small-6 columns value">${{ package.driverPayment.tip.toFixed(2) }}</div>
            </div>
            <div class="row breakdown">
              <div class="small-6 columns label">Pickup Fee:</div>
              <div class="small-6 columns value">${{ package.driverPayment.pickupFee.toFixed(2) }}</div>
            </div>
            <div class="row breakdown total">
              <div class="small-6 columns label">Total:</div>
              <div class="small-6 columns value">${{ getDriverPayment(package.driverPayment).toFixed(2) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- PAYMENT BREAKDOWN end -->
  </div>
</template>

<script>
export default {
  name: 'dispatch-reservation-details',
  props: [ 'package' ],
  data () {
    return {
      showPaymentBreakdown: false
    }
  },
  methods: {
    getAddress (location) {
      if (location.geofenceType === 'airport' &&
          location.geofenceName !== '') {
        let result = location.geofenceName
        if (location.terminal && location.terminal !== '' && location.terminal !== 'undefined') {
          result += ' - Terminal ' + location.terminal
        }
        return result
      }
      return location.title

      // let address = ''
      // if (location.address2 && location.address2 !== '' && location.address2 !== 'undefined') {
      //   address = address + location.address2 + ' '
      // }
      // if (location.address1) {
      //   address = address + location.address1 + ', '
      // }
      // if (location.city) {
      //   address = address + location.city + ' '
      // }
      // if (location.state) {
      //   address = address + location.state + ' '
      // }
      // if (location.zip) {
      //   address = address + location.zip
      // }

      return address
    },
    getDriverPayment (breakdown) {
      return breakdown.base + breakdown.bonus + breakdown.tip + breakdown.pickupFee
    },
    showBreakdown (value) { this.showPaymentBreakdown = value }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/_status';
@import '../../assets/scss/_service-types';
.package{
  padding: 0 20px 20px;

  & .package-id{
    font-size: 22px;
    font-weight: 700;
    margin-top: 20px;
    margin-bottom: 20px;

    & a{ color: #3F2975;
      & .fa-cube { color: #26D97A; }
    }
  }
}

.label-value{
  margin-top: 10px;
  & .label{
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 0;
    color: #8DA0A9;
  }

  & .value{
    color: #333;

    &.service-type{ color: #fff; }
    &.status{ color: #fff; font-size: 16px; }

    & .info {
      font-weight: 700;
      margin-top: 5px;
      margin-bottom: 10px;
      font-size: 14px;
    }
  }
}

a{
  font-weight: 700;
}

.divider{
  display: block;
  width: 350px;
  margin: 20px auto;
  border-top: 1px solid rgba(0,0,0,0.2);
}

.button.unassign { margin-top: 10px; }

.stops {

  & .stop {
    padding: 10px;
    border: 1px solid rgba(0,0,0,0.2);
    background-color: #fff;
    margin-bottom: 5px;
    border-radius: 3px;
    border-top: 3px solid #CA3538;
    position: relative;

    &.pickup{ border-top: 3px solid #2EE47D; }
  }
  & .title {
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 10px;
    color: #8DA0A9;
  }

  & .name {
    font-size: 14px;
    margin-bottom: 3px;
    & i {
      display: inline-block;
      margin-right: 2px;
      color: #CA3538;

      &.pickup{ color: #2EE47D; }
    }
  }

  & .time{
    font-size: 12px;
    font-weight: 700;
    color: #555;
  }

  & .address{
    margin-top: 5px;
    font-size: 16px;
    font-weight: 700;
    color: #555;
  }

  & .misc {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 14px;
    color: #555;
    text-align: right;
    & .rsvp-id{  }

    & .pax-luggage {
      color: #777;
      font-size: 12px;
      font-weight: 700;
      & * {
        display: inline-block;
        vertical-align: middle;
      }
      & i{
        margin-left: 5px;
        display: inline-block;
      }

      & span{ margin-left: 2px; }
    }
  }
}

a.payment-breakdown-button {
  font-size: 10px;
  display: block;
}


.modal-container {
  position: fixed;
  top: -100%; left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  background-color: rgba(0,0,0,0.4);
  opacity: 0;
  transition: all 250ms;

  &.show {
    opacity: 1;
    top: 0;
  }

  & .modal {
    display: table;
    width: 100%;
    height: 100%;
  }

  & .modal-content {
    display: table-cell;
    vertical-align: middle;
  }
}

.payment-breakdown {
  width: 300px;
  background-color: #fff;
  margin: 0 auto;
  border-radius: 3px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  overflow: hidden;
  position: relative;

  & .close {
    display: block;
    position: absolute;
    color: #fff;
    top: 0;
    right: 0;
    padding: 10px 10px;
  }

  & .title {
    background-color: #333;
    color: #fff;
    font-weight: 700;
    text-align: center;
    padding: 10px 5px;
  }

  & .breakdown {
    padding: 5px 15px;
    font-size: 14px;
    text-transform: uppercase;

    &.total{
      font-size: 16px;
      padding: 10px 0 10px;
      margin: 0 15px;
      border-top: 1px solid rgba(0,0,0,0.15);
    }
  }
  & .label {
    text-align: left;
    color: #555;
  }
  & .value {
    text-align: right;
    font-weight: 700;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Dispatch/PackageDetails.vue